import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface DashboardQuickActionsProps {
  canCreateSale: boolean;
  canReadSales: boolean;
  canReadProducts: boolean;
  canReadUsers: boolean;
}

export const DashboardQuickActions = ({
  canCreateSale,
  canReadSales,
  canReadProducts,
  canReadUsers,
}: DashboardQuickActionsProps) => {
  const { t } = useTranslation();
  const actions = [
    canCreateSale
      ? {
          to: '/admin/sales',
          icon: 'point_of_sale',
          label: t('admin.dashboard.quick_actions.sale'),
        }
      : null,
    canReadSales
      ? {
          to: '/admin/sales/history',
          icon: 'receipt_long',
          label: t('admin.dashboard.quick_actions.history'),
        }
      : null,
    canReadProducts
      ? {
          to: '/admin/products',
          icon: 'inventory_2',
          label: t('admin.dashboard.quick_actions.products'),
        }
      : null,
    canReadUsers
      ? {
          to: '/admin/users',
          icon: 'group',
          label: t('admin.dashboard.quick_actions.users'),
        }
      : null,
  ].filter((action): action is NonNullable<typeof action> => Boolean(action));

  return (
    <section className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-bold text-slate-950">
        {t('admin.dashboard.quick_actions.title')}
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        {t('admin.dashboard.quick_actions.description')}
      </p>

      {actions.length > 0 ? (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          {actions.map((action) => (
            <Link
              key={action.to}
              to={action.to}
              className="group flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
            >
              <span
                className="material-symbols-outlined flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-xl text-slate-600 transition-colors group-hover:bg-primary/10 group-hover:text-primary"
                aria-hidden="true"
              >
                {action.icon}
              </span>
              <span>{action.label}</span>
              <span className="material-symbols-outlined ml-auto text-lg" aria-hidden="true">
                arrow_forward
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-5 rounded-2xl bg-slate-50 px-4 py-5 text-sm text-slate-500">
          {t('admin.dashboard.quick_actions.empty')}
        </p>
      )}
    </section>
  );
};
